import { useEffect, useId, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import { useGetProfile, useUpdateAccount } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardHeader, CardTextContent, CardTitle } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Input } from "@/components/Input";
import { Subtitle } from "@/components/Subtitle";
import { Textarea } from "@/components/Textarea";

type ProfileSetupModalProps = {
  onComplete: () => void;
  open?: boolean;
};

export function ProfileSetupModal({ onComplete, open }: ProfileSetupModalProps) {
  const { t } = useTranslation();
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);

  const updateAccount = useUpdateAccount({
    mutation: { onSuccess: onComplete },
  });

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(e.currentTarget);
    const display_name = form.get("display_name")! as string;
    const real_name = form.get("real_name")! as string;
    const pronouns = form.get("pronouns")! as string;
    const biography = form.get("biography")! as string;
    updateAccount.mutate({
      data: {
        display_name,
        real_name,
        pronouns,
        biography,
        avatar_image_id: null,
      },
    });
  }

  useEffect(() => (open ? dialog?.showModal() : dialog?.close()), [dialog, open]);
  const labelId = useId();
  const descriptionId = useId();

  return (
    <Dialog ref={setDialog} className="w-lg" aria-labelledby={labelId}>
      <CardHeader>
        <CardTitle>
          <Subtitle id={labelId}>{t("profile-setup-title")}</Subtitle>
          <p id={descriptionId} className="font-disinterest text-placeholder">
            {t("profile-setup-subtitle")}
          </p>
        </CardTitle>
      </CardHeader>
      <CardTextContent>
        <Form
          error={updateAccount.error}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
        >
          <FormField title={t("display-name")}>
            <Input
              name="display_name"
              type="text"
              placeholder={me.data?.id}
              disabled={updateAccount.isPending}
              autoFocus
            />
          </FormField>
          <FormField title={t("real-name")}>
            <Input name="real_name" type="text" placeholder={t("real-name")} disabled={updateAccount.isPending} />
          </FormField>
          <FormField title={t("pronouns")}>
            <Input
              name="pronouns"
              type="text"
              placeholder={t("pronouns-placeholder")}
              disabled={updateAccount.isPending}
            />
          </FormField>
          <FormField title={t("biography")}>
            <Textarea
              name="biography"
              rows={4}
              placeholder={t("biography-placeholder")}
              disabled={updateAccount.isPending}
            />
          </FormField>
          <ActionBox>
            <Button type="button" onClick={onComplete} disabled={updateAccount.isPending}>
              {t("skip")}
            </Button>
            <Button type="submit" disabled={updateAccount.isPending}>
              {updateAccount.isPending ? t("saving") : t("save")}
            </Button>
          </ActionBox>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
